import { extractAppConfig } from '@/utils/app-config';
import { hasErrorField } from '@/utils/error';
import {
    EnumEventName,
    EventClientError,
    PropBaseEventProps,
} from '@calm/evexia-analytics';
import { Environment } from '@rhg/constants';
import { createDestinations } from '@rhg/libs/analytics/destination';
import mapBaseEventProps from '@rhg/libs/analytics/map-base-event-props';
import { logger } from '@rhg/libs/logger';
import React from 'react';
import GeneralError from '../general-error';

type ErrorBoundaryProps =
    | {
          appConfig: ServerRuntimeConfig;
          children?: React.ReactNode;
      }
    | {
          children?: React.ReactNode;
      };

interface ErrorBoundaryState {
    hasError: boolean;
}

const {
    NEXT_PUBLIC_ENV: env,
    NEXT_PUBLIC_ANALYTICS_DEBUG: debug,
    NEXT_PUBLIC_AMPLITUDE_TOKEN: amplitudeToken,
    NEXT_PUBLIC_AMPLITUDE_BASEURL: amplitudeBaseUrl,
} = extractAppConfig();

const destinations = createDestinations({
    amplitudeToken,
    amplitudeBaseUrl,
    env: env as Environment,
    debug,
});

const createErrorProps = (
    error: unknown
): EventClientError | PropBaseEventProps => {
    if (hasErrorField(error, 'message') && error.message) {
        return { Description: `${error.message}` };
    }
    return {};
};

/**
 * Only a react class component can be used as an error boundary.
 * https://reactjs.org/docs/error-boundaries.html
 */
export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    Readonly<ErrorBoundaryState>
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    private errorLogger = (error: unknown) => {
        const payload = createErrorProps(error);
        const mappedPayload = mapBaseEventProps(payload);
        destinations.forEach((destination) => {
            if (destination.isLoaded()) {
                destination
                    .track(EnumEventName.ClientError, mappedPayload)
                    .catch((err) =>
                        logger.error(
                            'Failed to track ClientError',
                            {},
                            err as Error
                        )
                    );
            }
        });
    };

    componentDidCatch(error: Error) {
        this.errorLogger(error);
    }

    private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
        this.errorLogger(event.reason);
    };

    private windowErrorHandler = (event: ErrorEvent) => {
        this.errorLogger(event.error);
    };

    componentDidMount() {
        window.addEventListener(
            'unhandledrejection',
            this.promiseRejectionHandler
        );
        window.addEventListener('error', this.windowErrorHandler);
    }

    componentWillUnmount() {
        window.removeEventListener(
            'unhandledrejection',
            this.promiseRejectionHandler
        );
        window.removeEventListener('error', this.windowErrorHandler);
    }

    render() {
        const { hasError } = this.state;
        const { children } = this.props;

        return hasError ? <GeneralError /> : children;
    }
}

export default ErrorBoundary;
